/* eslint-disable react-hooks/exhaustive-deps */
import { format } from 'date-fns';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { RootState } from '../../../../setup';
import { ScrollComponent } from '../../../../_start/assets/ts/components';
import {
  getConfig,
  IThemeConfig,
  useTheme,
} from '../../../../_start/layout/core';
import { WEB_URL } from '../../../support/api';
import {
  failureToast,
  successToast,
} from '../../../support/utils';
import { getAllPartner } from '../../client-management/redux/PartnerCRUD';
import { getAllPackage } from '../../content-management/redux/PackageCRUD';
import { getAllProduct } from '../../content-management/redux/ProductCRUD';
import { ContentErrorBoundary } from '../../edit-content/ContentErrorBoundary';
import { ElementDropContainer } from '../../edit-content/elements/ElementDropContainer';
import {
  setAllEmployees,
  setAllPackages,
  setAllPartners,
  setAllPosts,
  setAllProducts,
  setContent,
  setFeaturedPackages,
  setFeaturedProducts,
  setMediaLibrary,
  setPage,
  setSvgLibrary,
} from '../../edit-content/redux/EditContentActions';
import { getAllEmployee } from '../../employee-management/redux/EmployeeCRUD';
import { listMedia } from '../../media-management/redux/MediaLibraryCRUD';
import { listSvg } from '../../media-management/redux/SvgLibraryCRUD';
import { getAllPost } from '../../post-management/redux/PostsCRUD';
import { IPage, PageStatusType } from '../models/Page';
import { publishPage, updatePage, viewPage } from '../redux/PageCRUD';

const defaultPageConfig = getConfig();
const pageConfig: Partial<IThemeConfig> = {
  sidebar: {
    ...defaultPageConfig.sidebar,
    display: true,
    content: 'edit-content',
    bgColor: 'bg-white',
  },
};

export const PageEditContent = () => {
  const { id } = useParams<{ id: string }>();

  const dispatch = useDispatch();

  const page = useSelector((state: RootState) => state.editContent.page);

  const parentPage = page.parent;

  const content = useSelector((state: RootState) => state.editContent.content);

  const featuredPackages = useSelector(
    (state: RootState) => state.editContent.featuredPackages
  );

  const featuredProducts = useSelector(
    (state: RootState) => state.editContent.featuredProducts
  );

  const [loading, setLoading] = useState(false);

  // Collect package IDs from form-package blocks in content (so pivot is populated even when Redux was empty on load)
  const getFeaturedPackageIdsFromContent = (): number[] => {
    const ids: number[] = [];
    (content || []).forEach((element: { name?: string; featured_packages?: Array<{ id: number } | number> }) => {
      if (element.name !== 'form-package' || !element.featured_packages) return;
      element.featured_packages.forEach((p: { id: number } | number) => {
        const id = typeof p === 'object' && p && 'id' in p ? p.id : p;
        if (id && !ids.includes(Number(id))) ids.push(Number(id));
      });
    });
    return ids;
  };

  // SUBMIT
  const handleSave = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    setLoading(true);

    const publish_date = format(
      new Date(page.publish_date),
      'yyyy-MM-dd HH:mm:ss'
    );

    const fromRedux = (featuredPackages ?? []).map((element: { id: number }) => element.id);
    const fromContent = getFeaturedPackageIdsFromContent();
    const featuredPackageIds = [...new Set([...fromRedux, ...fromContent])];

    const fromReduxProducts = (featuredProducts ?? []).map((element: { id: number }) => element.id);
    const fromContentProducts = (content || []).flatMap(
      (el: { name?: string; featured_products?: Array<{ id: number } | number> }) =>
        el.name === 'featured-products' && el.featured_products
          ? el.featured_products.map((p: { id: number } | number) =>
              typeof p === 'object' && p && 'id' in p ? p.id : p
            )
          : []
    );
    const featuredProductIds = [...new Set([...fromReduxProducts, ...fromContentProducts])];

    try {
      const payload: IPage = {
        ...page,
        content: content,
        publish_date,
        // @ts-ignore
        featured_packages: featuredPackageIds,
        // @ts-ignore
        featured_products: featuredProductIds,
      };

      await updatePage(payload);
      successToast('Content has been updated.');
    } catch (error) {
      failureToast(error);
    } finally {
      setLoading(false);
    }
  };

  // INITIALIZATION
  const handleFetch = async (id: string) => {
    setLoading(true);

    try {
      dispatch(setContent([]));

      const [
        page,
        allPosts,
        allEmployees,
        allPackages,
        allPartners,
        allProducts,
      ] = await Promise.all([
        viewPage(id),
        getAllPost(),
        getAllEmployee(),
        getAllPackage(),
        getAllPartner(),
        getAllProduct(),
      ]);

      const [paginatedMedia, paginatedSvg] = await Promise.all([
        listMedia(),
        listSvg(),
      ]);

      // media library
      dispatch(setMediaLibrary(paginatedMedia));
      dispatch(setSvgLibrary(paginatedSvg));

      dispatch(setAllPosts(allPosts)); // set ALL POST data
      dispatch(setAllEmployees(allEmployees)); // // set ALL EMPLOYEE data
      dispatch(setAllPackages(allPackages));
      dispatch(setAllProducts(allProducts));

      dispatch(setAllPartners(allPartners));

      dispatch(setPage(page)); // set Page data
      dispatch(setContent(page.content)); // set CONTENT data
      dispatch(setFeaturedPackages(page.featured_packages ?? []));
      dispatch(setFeaturedProducts(page.featured_products ?? []));

      setLoading(false);
    } catch (error) {
      failureToast(error);
      setLoading(false);
    }
  };

  const { setTheme } = useTheme();
  // Refresh UI after config updates
  useEffect(() => {
    setTheme(pageConfig);
    setTimeout(() => {
      ScrollComponent.reinitialization();
    }, 100);

    if (id) {
      handleFetch(id);
    }

    return () => {
      setTheme(defaultPageConfig);
    };
  }, [id]);

  const handlePublish = async () => {
    setLoading(true);

    try {
      const result = await publishPage(page);
      dispatch(setPage(result));
      successToast('Page has been published.');
      setLoading(false);
    } catch (error) {
      failureToast(error);
      setLoading(false);
    }
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center">
        {/* begin::Page Title */}
        <div className="align-items-center">
          <h2>{page.title}</h2>
        </div>
        {/* end::Page Title */}

        {/* begin::Actions */}
        <div className="d-inline-flex align-items-center">
          <form onSubmit={handleSave} className="me-8">
            {page.status === PageStatusType.DRAFT && (
              <button
                type={'button'}
                className="btn btn-active-light-warning"
                disabled={loading}
                onClick={handlePublish}
              >
                Publish
              </button>
            )}
            <a
              aria-disabled
              className={`btn btn-active-light-success`}
              href={
                parentPage
                  ? `${WEB_URL}/${parentPage.slug}/${page.slug}`
                  : `${WEB_URL}/${page.slug}`
              }
              target="_blank"
              rel="noreferrer"
            >
              Preview
            </a>
            <button
              type="submit"
              disabled={loading}
              className="btn btn-active-light-primary"
            >
              Save
            </button>
          </form>

          {/* begin::Toggle Sidebar */}
          {/* <button
						onClick={toggleSidebarDisplay}
						className="btn btn-icon btn-sm btn-white btn-active-primary"
						title="Toggle Sidebar"
					>
						{config.sidebar.display ? (
							<KTSVG
								path="/media/icons/duotone/Navigation/Right-2.svg"
								className="svg-icon-1 svg-icon-dark"
							/>
						) : (
							<KTSVG
								path="/media/icons/duotone/Navigation/Left-2.svg"
								className="svg-icon-1 svg-icon-dark"
							/>
						)}
					</button> */}
          {/* end::Toggle Sidebar */}
        </div>
        {/* end::Actions */}
      </div>

      {/* begin::Card */}
      <div className={`mt-3 card ${loading ? 'overlay overlay-block' : ''}`}>
        {/* begin::Card Body */}
        <div className={`card-body p-20 ${loading ? 'overlay-wrapper' : ''}`}>
          {/* begin::Element Drop Container */}
          <ContentErrorBoundary>
            <ElementDropContainer />
          </ContentErrorBoundary>
          {/* end::Element Drop Container */}

          {/* begin::Overlay Layer */}
          {loading && (
            <div className="overlay-layer rounded bg-dark bg-opacity-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          )}
          {/* begin::Overlay Layer */}
        </div>
        {/* end::Card Body */}
      </div>
      {/* end::Card */}
    </>
  );
};
